import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	createAiManagedObject,
	fetchAiManagedObject,
	updateAiManagedObject,
} from '@atlassian/ai-managed-objects/api';
import type {
	AiManagedObject,
	AiManagedObjectCreateInput,
	AiManagedObjectUpdateInput,
} from '@atlassian/ai-managed-objects/types';

import { cacheAiManagedObject as cacheAiManagedObjectCommand } from './editor-commands/configure-popup';
import { getCachedAiManagedObjectFromState } from './pm-plugins/state';

export const getCachedAiManagedObject = (
	editorView: EditorView,
	aimoId: string,
): AiManagedObject | undefined => getCachedAiManagedObjectFromState(editorView.state, aimoId);

export const cacheAiManagedObject = (
	editorView: EditorView,
	aimo: AiManagedObject,
	localId?: string,
): void => {
	const tr = cacheAiManagedObjectCommand(aimo, localId)({ tr: editorView.state.tr });
	if (tr) {
		editorView.dispatch(tr);
	}
};

export const getAiManagedObjectWithCache = async ({
	aimoId,
	cachedAimo,
	editorView,
	localId,
}: {
	aimoId: string;
	cachedAimo?: AiManagedObject;
	editorView: EditorView;
	localId?: string;
}): Promise<AiManagedObject | null> => {
	const existingAimo = cachedAimo ?? getCachedAiManagedObject(editorView, aimoId);
	if (existingAimo) {
		if (localId) {
			cacheAiManagedObject(editorView, existingAimo, localId);
		}
		return existingAimo;
	}

	const fetchedAimo = await fetchAiManagedObject(aimoId);
	if (fetchedAimo) {
		cacheAiManagedObject(editorView, fetchedAimo, localId);
	}

	return fetchedAimo;
};

export const createAiManagedObjectWithCache = async (
	editorView: EditorView,
	input: AiManagedObjectCreateInput,
	localId?: string,
): Promise<AiManagedObject> => {
	const aimo = await createAiManagedObject(input);
	cacheAiManagedObject(editorView, aimo, localId);
	return aimo;
};

export const updateAiManagedObjectWithCache = async (
	editorView: EditorView,
	input: AiManagedObjectUpdateInput,
	localId?: string,
): Promise<AiManagedObject> => {
	const aimo = await updateAiManagedObject(input);
	cacheAiManagedObject(editorView, aimo, localId);
	return aimo;
};
